/**
 * CRM Settings — Inbound Webhooks
 *
 * Manage inbound webhook sources that capture leads from external
 * systems. Secrets are one-time reveal; rows expand to show recent
 * deliveries with the processing result and any error message.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ChevronDown, ChevronRight, Copy, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/crm/webhooks')({
  component: WebhooksPage,
})

interface WebhookSource {
  id: string
  name: string
  token: string
  allowedOrigins: string[] | null
  rateLimitPerMin: number
  assignToUserId: string | null
  enabled: boolean
  lastReceivedAt: Date | null
  receiptCount: number
}

interface Delivery {
  id: string
  processingResult: string
  leadId: string | null
  errorMessage: string | null
  receivedAt: Date
}

function WebhooksPage() {
  const t = useTranslations()
  const qc = useQueryClient()
  const listQ = useQuery(orpc.crm.webhooks.list.queryOptions({ input: {} }))
  const sources = (listQ.data ?? []) as WebhookSource[]

  const [expanded, setExpanded] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newRate, setNewRate] = useState(60)
  const [secretReveal, setSecretReveal] = useState<{ secret: string; name: string } | null>(null)
  const [editSource, setEditSource] = useState<WebhookSource | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    id: string | null
    name: string
  }>({
    open: false,
    id: null,
    name: '',
  })

  const invalidateList = () => qc.invalidateQueries({ queryKey: orpc.crm.webhooks.list.queryKey() })

  const createMut = useMutation(
    orpc.crm.webhooks.create.mutationOptions({
      onSuccess: (data: unknown) => {
        const d = data as { secret: string; name: string }
        toast.success(t('crm.settings.webhooks.newWebhook'))
        setCreateOpen(false)
        setNewName('')
        setNewRate(60)
        setSecretReveal({ secret: d.secret, name: d.name })
        invalidateList()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateMut = useMutation(
    orpc.crm.webhooks.update.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.common.save'))
        setEditSource(null)
        invalidateList()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMut = useMutation(
    orpc.crm.webhooks.delete.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.common.delete'))
        invalidateList()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const copy = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(
      () => toast.success(`${label}`),
      () => toast.error(t('crm.common.delete')),
    )
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('crm.settings.webhooks.title')}</h1>
          <p className="text-muted-foreground">{t('navigation.crmSettings')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('crm.settings.webhooks.newWebhook')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="p-6 space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : sources.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              {t('crm.common.noRecords')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>{t('crm.account.name')}</TableHead>
                  <TableHead>{t('crm.settings.webhooks.token')}</TableHead>
                  <TableHead>{t('crm.settings.webhooks.rateLimitPerMin')}</TableHead>
                  <TableHead>{t('crm.common.status')}</TableHead>
                  <TableHead>{t('crm.settings.webhooks.lastReceived')}</TableHead>
                  <TableHead>{t('crm.settings.webhooks.receiptCount')}</TableHead>
                  <TableHead className="text-right">{t('crm.common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.map((s) => (
                  <WebhookRow
                    key={s.id}
                    source={s}
                    expanded={expanded === s.id}
                    onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
                    onCopy={copy}
                    onEdit={() => setEditSource(s)}
                    onDelete={() => setDeleteDialog({ open: true, id: s.id, name: s.name })}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('crm.settings.webhooks.newWebhook')}</DialogTitle>
            <DialogDescription>{t('crm.settings.webhooks.secretRevealHelp')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="w-name">{t('crm.account.name')}</Label>
              <Input
                id="w-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Landing page form"
              />
            </div>
            <div>
              <Label htmlFor="w-rate">{t('crm.settings.webhooks.rateLimitPerMin')}</Label>
              <Input
                id="w-rate"
                type="number"
                min={1}
                max={10000}
                value={newRate}
                onChange={(e) => setNewRate(Number(e.target.value))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('crm.common.cancel')}
            </Button>
            <Button
              onClick={() => createMut.mutate({ name: newName, rateLimitPerMin: newRate })}
              disabled={!newName || createMut.isPending}
            >
              {createMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('crm.common.create')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time secret reveal */}
      <Dialog open={secretReveal !== null} onOpenChange={() => setSecretReveal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('crm.settings.webhooks.secretRevealTitle')}</DialogTitle>
            <DialogDescription>{t('crm.settings.webhooks.secretRevealHelp')}</DialogDescription>
          </DialogHeader>
          {secretReveal && (
            <div className="space-y-4">
              <div>
                <Label>{t('crm.account.name')}</Label>
                <p className="text-sm font-medium">{secretReveal.name}</p>
              </div>
              <div>
                <Label>{t('crm.settings.webhooks.copySecret')}</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-muted rounded text-xs break-all">
                    {secretReveal.secret}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copy(secretReveal.secret, t('crm.settings.webhooks.copySecret'))}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setSecretReveal(null)}>{t('crm.common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      {editSource && (
        <EditWebhookDialog
          source={editSource}
          onClose={() => setEditSource(null)}
          onSave={(patch) => updateMut.mutate({ id: editSource.id, ...patch })}
          saving={updateMut.isPending}
        />
      )}

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          setDeleteDialog({
            open,
            id: open ? deleteDialog.id : null,
            name: open ? deleteDialog.name : '',
          })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('crm.common.delete')} "{deleteDialog.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>{t('crm.common.required')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('crm.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={() => {
                if (deleteDialog.id) deleteMut.mutate({ id: deleteDialog.id })
                setDeleteDialog({ open: false, id: null, name: '' })
              }}
            >
              {t('crm.common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function WebhookRow({
  source,
  expanded,
  onToggle,
  onCopy,
  onEdit,
  onDelete,
}: {
  source: WebhookSource
  expanded: boolean
  onToggle: () => void
  onCopy: (v: string, label: string) => void
  onEdit: () => void
  onDelete: () => void
}) {
  const t = useTranslations()
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </TableCell>
        <TableCell className="font-medium">{source.name}</TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <code className="text-xs">{source.token.slice(0, 12)}…</code>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => onCopy(source.token, t('crm.settings.webhooks.token'))}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        </TableCell>
        <TableCell>{source.rateLimitPerMin}</TableCell>
        <TableCell>
          {source.enabled ? (
            <Badge className="bg-green-500">{t('crm.settings.lookups.active')}</Badge>
          ) : (
            <Badge variant="secondary">{t('crm.common.status')}</Badge>
          )}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {source.lastReceivedAt ? new Date(source.lastReceivedAt).toLocaleString() : '-'}
        </TableCell>
        <TableCell>{source.receiptCount}</TableCell>
        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" className="text-red-600" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/30 p-4">
            <DeliveriesPanel webhookSourceId={source.id} />
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function DeliveriesPanel({ webhookSourceId }: { webhookSourceId: string }) {
  const t = useTranslations()
  const q = useQuery(
    orpc.crm.webhooks.deliveries.queryOptions({
      input: { webhookSourceId, limit: 50 },
    }),
  )
  const deliveries = (q.data ?? []) as Delivery[]

  if (q.isLoading) return <Skeleton className="h-20 w-full" />
  if (deliveries.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('crm.common.noRecords')}</p>
  }

  return (
    <div>
      <h4 className="font-medium mb-2">{t('crm.settings.webhooks.deliveries')}</h4>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('crm.settings.webhooks.lastReceived')}</TableHead>
            <TableHead>{t('crm.activity.outcome')}</TableHead>
            <TableHead>{t('crm.lead.entity')}</TableHead>
            <TableHead>{t('crm.common.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deliveries.map((d) => (
            <TableRow key={d.id}>
              <TableCell className="text-sm">{new Date(d.receivedAt).toLocaleString()}</TableCell>
              <TableCell>
                <Badge
                  variant={d.processingResult === 'created_lead' ? 'default' : 'secondary'}
                  className={d.processingResult === 'created_lead' ? 'bg-green-500' : ''}
                >
                  {d.processingResult}
                </Badge>
              </TableCell>
              <TableCell className="text-xs font-mono">
                {d.leadId ? d.leadId.slice(0, 8) : '-'}
              </TableCell>
              <TableCell className="text-sm text-red-600 max-w-md truncate">
                {d.errorMessage ?? '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function EditWebhookDialog({
  source,
  onClose,
  onSave,
  saving,
}: {
  source: WebhookSource
  onClose: () => void
  onSave: (patch: {
    name?: string
    rateLimitPerMin?: number
    allowedOrigins?: string[]
    assignToUserId?: string
    enabled?: boolean
  }) => void
  saving: boolean
}) {
  const t = useTranslations()
  const [name, setName] = useState(source.name)
  const [rateLimitPerMin, setRate] = useState(source.rateLimitPerMin)
  const [allowedOrigins, setOrigins] = useState((source.allowedOrigins ?? []).join('\n'))
  const [assignToUserId, setAssignee] = useState(source.assignToUserId ?? '')
  const [enabled, setEnabled] = useState(source.enabled)

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('crm.common.edit')} {t('navigation.webhooks').toLowerCase()}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="e-name">{t('crm.account.name')}</Label>
            <Input id="e-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="e-rate">{t('crm.settings.webhooks.rateLimitPerMin')}</Label>
            <Input
              id="e-rate"
              type="number"
              min={1}
              value={rateLimitPerMin}
              onChange={(e) => setRate(Number(e.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="e-origins">{t('crm.common.optional')}</Label>
            <textarea
              id="e-origins"
              className="w-full min-h-[80px] rounded-md border bg-background px-3 py-2 text-sm"
              value={allowedOrigins}
              onChange={(e) => setOrigins(e.target.value)}
              placeholder="https://example.com"
            />
          </div>
          <div>
            <Label htmlFor="e-assignee">{t('crm.common.assignedTo')}</Label>
            <Input
              id="e-assignee"
              value={assignToUserId}
              onChange={(e) => setAssignee(e.target.value)}
              placeholder={t('crm.common.optional')}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <Label>{t('crm.settings.lookups.active')}</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('crm.common.cancel')}
          </Button>
          <Button
            onClick={() => {
              const origins = allowedOrigins
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean)
              onSave({
                name,
                rateLimitPerMin,
                allowedOrigins: origins,
                assignToUserId: assignToUserId || undefined,
                enabled,
              })
            }}
            disabled={saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('crm.common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
